import { Component, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/** The shared "Loading…" shown while a code-split page or panel downloads. */
export function LoadingFallback() {
  return (
    <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>
  );
}

/**
 * Catches a page that failed to load or render, so one page's error never blanks the whole app (without a
 * boundary React unmounts everything). A failed chunk usually means Cosmos was updated under an open tab —
 * main.tsx reloads once for that; this is the fallback if it happens again. Resets when the route changes.
 */
class PageErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page failed to load', error, info.componentStack);
  }

  componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const stale = /dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(error.message);
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-20 text-center text-sm">
        <p className="font-medium text-foreground">{stale ? 'Cosmos has been updated' : 'This page failed to load'}</p>
        <p className="text-muted-foreground">
          {stale ? 'Reload to get the latest version.' : error.message || 'Something went wrong.'}
        </p>
        <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Reload
        </Button>
      </div>
    );
  }
}

/** Suspense + error boundary for `React.lazy` pages (routes in main.tsx, lazy panels). */
export function LazyPage({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <PageErrorBoundary resetKey={pathname}>
      <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
    </PageErrorBoundary>
  );
}
